'use client';

import clsx from 'clsx';
import Image from 'next/image';
import { FC, useState } from 'react';

interface IAvatarProps {
  src?: string;
  width?: number;
  height?: number;
  rounded?: boolean;
  className?: string;
  firstName?: string;
  lastName?: string;
  initials?: string;
}

const Avatar: FC<IAvatarProps> = ({
  src,
  width = 40,
  height = 40,
  rounded = true,
  className = '',
  firstName = '',
  lastName = '',
  initials = '',
}) => {
  const [imgError, setImgError] = useState(false);

  // If we have name/initials, show initials avatar
  if (firstName || lastName || initials)
    return (
      <div
        className={clsx(
          'rounded-full w-10 h-10 bg-primary-700 text-white flex justify-center items-center font-medium border border-white',
          className,
        )}
      >
        {initials && initials}
        {(firstName?.[0] || '').toUpperCase()}
        {(lastName?.[0] || '').toUpperCase()}
      </div>
    );

  const defaultAvatar = (
    <div
      className={clsx(
        'object-cover block avatar bg-slate-200 flex items-center justify-center',
        rounded ? 'rounded-full' : '',
        className,
      )}
      style={{ width, height }}
    >
      <svg
        className='w-1/2 h-1/2 text-slate-400'
        fill='currentColor'
        viewBox='0 0 20 20'
      >
        <path
          fillRule='evenodd'
          d='M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z'
          clipRule='evenodd'
        />
      </svg>
    </div>
  );

  // If src is empty, invalid, or failed to load, show the default avatar
  if (!src || src.trim() === '' || imgError) return defaultAvatar;

  return (
    <Image
      className={clsx('object-cover block avatar', rounded ? 'rounded-full' : '', className)}
      alt='Avatar'
      width={width}
      height={height}
      src={src}
      onError={() => setImgError(true)}
    />
  );
};

export default Avatar;
